import { z } from 'zod';
import { ApiError } from './errors.js';
import { defaultItemOption } from './options.js';
/**
 * The ordering module's vocabulary (spec §3.6) — types, the lifecycle, and the
 * arithmetic. No I/O, no SQL, no HTTP.
 *
 * This file exists in `packages/domain` for the reason `payments.ts` gives one
 * table over: **more than one surface will need the same answer and none of them
 * may be the authority for the others.** `core-api` prices a staff-entered
 * ticket today; the guest ordering page and the partner's `POST /orders` will
 * price the identical basket tomorrow. A second implementation of "a burger plus
 * two sides, one of which is a two-dollar credit" would eventually disagree with
 * the first, and the disagreement would be a guest charged the wrong amount by
 * whichever surface they happened to use.
 *
 * ## The rule this file is really about
 *
 * Spec §3.6: **"The server prices everything."** It is B7 generalized, and §3.6
 * says why it matters more here than anywhere else — "an order is the most
 * price-manipulable object in the system and the caller most likely to be
 * manipulated is a persuadable model; there is no code path where the request
 * sets a cent."
 *
 * So `priceOrder` takes a *catalog* and a *basket of ids*, and there is no
 * parameter through which a price could arrive. A caller holding a number a
 * request gave it has nothing to do with it.
 */
/* ------------------------------------------------------------------ */
/* The lifecycle                                                       */
/* ------------------------------------------------------------------ */
/**
 * Spec §3.6's chain, value for value: `placed → confirmed → preparing → ready →
 * completed | cancelled`.
 *
 * Six states rather than a boolean pair, because each one is a different
 * sentence somebody says out loud. `placed` is "the ticket is in"; `confirmed`
 * is a human promising a time (the §3.6 `staff_confirm` default, and what
 * `notifications` texts the guest); `preparing` is the kitchen holding it;
 * `ready` is the counter calling a name; `completed` is the bag gone.
 * Collapsing any two would lose the moment a message is sent.
 */
export const OrderStatus = z.enum([
    'placed',
    'confirmed',
    'preparing',
    'ready',
    'completed',
    'cancelled',
    /* ---------------------------------------------------------------- */
    /* The fulfilled lane (docs/fulfillment-design.md decision 5)         */
    /* ---------------------------------------------------------------- */
    /**
     * The money has arrived and the whole bill is covered, so the thing may
     * now be *made*. It is a distinct state from `confirmed` because nobody
     * promised anything: a merch order is not confirmed by a human, it is paid
     * by a guest, and the difference is which event moves it.
     */
    'paid',
    /** The manufacturer has the order and gave us their id for it. */
    'submitted',
    /**
     * We took the money and could not hand the order to the manufacturer, after
     * the retry budget ran out.
     *
     * A state rather than a log line, and the design doc says why in as many
     * words: *"a log line is not custody of a family's $70 canvas."* This is the
     * owner's task — the one status in the table whose existence is an
     * instruction to a human.
     */
    'submit_failed',
    'shipped',
    'delivered',
]);
/**
 * The kitchen's lane: an order somebody cooks and hands over a counter.
 *
 * Named as a set because the **partition** is the property that matters — see
 * `ORDER_TRANSITIONS` below, and the test that walks it.
 */
export const ORDER_KITCHEN_STATUSES = [
    'confirmed',
    'preparing',
    'ready',
    'completed',
];
/** The fulfilled lane: an order somebody else makes and a courier delivers. */
export const ORDER_FULFILLMENT_STATUSES = [
    'paid',
    'submitted',
    'submit_failed',
    'shipped',
    'delivered',
];
/**
 * The two states both lanes share: where every order starts, and the way out
 * that is not a completion.
 */
export const ORDER_SHARED_STATUSES = ['placed', 'cancelled'];
/**
 * Who placed it — the same shape of vocabulary `booking_source` carries, and
 * deliberately a *different* enum from it.
 *
 * Three values, not five: an order has no `walk_in` (a walk-in at the counter is
 * a `staff` order typed on the till) and no `import` (LoveSpa is a spa and has
 * never had an order). Reusing `booking_source` would have bought two values
 * that can never occur and a shared enum that a later booking-only value would
 * silently widen.
 */
export const OrderSource = z.enum(['staff', 'guest_web', 'partner']);
/**
 * What may become what.
 *
 * A table rather than a chain of `if`s, because it is the *whole* rule and a
 * reader should be able to see all of it at once — and because a property test
 * can walk it (`ordering.test.ts`: no path leaves a terminal state).
 *
 * Two decisions worth reading twice:
 *
 *  * **Forward one rung at a time.** A kitchen that taps "ready" on an order it
 *    never confirmed has an order whose guest was never told a time, and §3.6
 *    makes that promise the confirmation step's whole job. The refusal names the
 *    state that *is* legal, so the screen can offer it.
 *  * **`cancelled` from every live state, including `ready`.** Food goes cold
 *    and guests do not come. A lifecycle with no way out of `ready` would leave
 *    the pass full of tickets nobody can close.
 *
 * `completed` and `cancelled` are terminal and have no successor at all. An
 * order that could be un-cancelled would be an order whose `order.updated`
 * stream tells the partner a different story depending on when they read it.
 *
 * ## The second lane, and why it does not leak into the first
 *
 * `docs/fulfillment-design.md` decision 5 is emphatic that merch does **not**
 * get a second order concept: *"One order concept… new statuses ride the
 * existing table's discipline."* So the fulfilled lane is five more rows here
 * rather than a parallel table with a parallel guard.
 *
 * The thing that makes one table safe for two businesses is that the lanes are
 * **disjoint after `placed`**. Both start there, because both are a promise
 * somebody made; from that one fork they never meet again:
 *
 * ```
 *                 ┌── confirmed → preparing → ready → completed        (kitchen)
 *   placed ───────┤
 *                 └── paid ⇄ submit_failed → submitted → shipped → delivered
 * ```
 *
 * That is a **property of this table**, not a rule enforced somewhere else, and
 * `ordering.test.ts` walks it in both directions: nothing reachable from
 * `confirmed` is a fulfilment status, and nothing reachable from `paid` is a
 * kitchen status. The alternative — one flat set of statuses plus a check
 * elsewhere that "a prepared order may not become `shipped`" — would have put
 * the rule in a second place, which is the shape this file exists to avoid.
 *
 * Three rows in the fulfilled lane are worth reading twice:
 *
 *  * **`paid → submit_failed → submitted`.** The retry budget is spent while
 *    the order is still `paid`; `submit_failed` is what the drain writes when
 *    the budget runs out, and it is *not* terminal, because the operator replay
 *    (`yourstore fulfillment submit --order …`) has to be able to land it.
 *    A terminal failure state would mean money taken and no way back.
 *  * **`shipped` cannot be cancelled.** Every other live state can, and this is
 *    the one deliberate exception in the table: once a courier has the parcel,
 *    the thing that happens next is a return and a refund, which are events of
 *    their own and not this order un-happening. Writing `cancelled` there would
 *    let a screen tell a guest their parcel is not coming while it is on a van.
 *  * **`delivered` is terminal**, like `completed` and for the same reason.
 */
export const ORDER_TRANSITIONS = {
    placed: ['confirmed', 'paid', 'cancelled'],
    confirmed: ['preparing', 'cancelled'],
    preparing: ['ready', 'cancelled'],
    ready: ['completed', 'cancelled'],
    completed: [],
    paid: ['submitted', 'submit_failed', 'cancelled'],
    submit_failed: ['submitted', 'cancelled'],
    submitted: ['shipped', 'cancelled'],
    shipped: ['delivered'],
    delivered: [],
    cancelled: [],
};
/**
 * **What a person may ask for from here** — the table, minus the fulfilled
 * lane.
 *
 * `ORDER_TRANSITIONS` is the whole rule about what may *follow* what, and it has
 * to contain `placed → paid` because that move genuinely happens. But nobody
 * *asks* for it: every status in the fulfilled lane is written by a machine —
 * `paid` by a capture committing, `submitted` and `submit_failed` by the
 * submission drain, `shipped` by a shipment notice read back from the
 * manufacturer, and `delivered` by nothing at all yet (see the enum). A screen
 * that offered "Paid" beside "Confirm" on a burger would let a kitchen tablet
 * strand a ticket in a lane it can never leave.
 *
 * So the staff wire's `next_statuses` and the transition route's accepted values
 * both come from **this** function, and the difference between the two lists is
 * the difference between "what can happen" and "what you may do". `cancelled`
 * survives the filter because it is shared: an owner really can cancel a merch
 * order that has not shipped.
 */
export function operatorNextStatuses(status) {
    return ORDER_TRANSITIONS[status].filter((next) => !ORDER_FULFILLMENT_STATUSES.includes(next));
}
/**
 * The statuses a human may name on a request. Derived from the lanes so that a
 * value added to one is not silently typeable on the other.
 */
export const OperatorOrderStatus = z.enum([
    ...ORDER_KITCHEN_STATUSES,
    ...ORDER_SHARED_STATUSES.filter((s) => s !== 'placed'),
]);
/** The two states nothing leaves. Derived from the table, never restated. */
export const ORDER_TERMINAL_STATUSES = OrderStatus.options.filter((status) => ORDER_TRANSITIONS[status].length === 0);
export function orderStatusIsTerminal(status) {
    return ORDER_TRANSITIONS[status].length === 0;
}
export function canTransitionOrder(from, to) {
    return ORDER_TRANSITIONS[from].includes(to);
}
/**
 * The guarded move, as the one refusal every surface raises.
 *
 * `409`, not `400`: the caller asked for something that was true a moment ago or
 * would be true in another order, which is exactly what the agreement reserves
 * `conflict` for — re-read the state and try again. A `400` would tell a kitchen
 * screen it had sent a malformed request when what it had was a stale one.
 */
export function assertOrderTransition(from, to) {
    if (canTransitionOrder(from, to))
        return;
    // **`allowed` is what a caller may *ask for*, not the whole table.** The
    // detail on this error is read by a screen deciding which buttons to offer,
    // and offering "Paid" on a burger would let somebody strand a ticket in a lane
    // it can never leave (see `operatorNextStatuses`). The complete rule is
    // `ORDER_TRANSITIONS`, one export up, for anything that needs it.
    const allowed = operatorNextStatuses(from);
    throw ApiError.conflict(allowed.length === 0
        ? `That order is already ${from}, and nothing follows it.`
        : `An order that is ${from} cannot become ${to}.`, {
        reason: orderStatusIsTerminal(from) ? 'terminal' : 'wrong_status',
        status: from,
        requested: to,
        allowed,
    });
}
/* ------------------------------------------------------------------ */
/* What a caller may ask for                                           */
/* ------------------------------------------------------------------ */
/**
 * One line of a basket: **an item id, a count, and the choices made about it.**
 *
 * There is no `unit_price_cents` here and there never may be one. That absence
 * is the schema-level statement of §3.6's pricing rule — a request that carried
 * a price would not be refused by a check somewhere, it would fail to parse.
 */
/**
 * **A line note — "no onions", "sauce on the side", "no salt, thanks".**
 *
 * The owner, 2026-09-18: a guest has to be able to say something about *one
 * dish*, not only about the ticket. `orders.notes` has always existed and is
 * the wrong place for it — a ticket-wide "no onions" on an order of four dishes
 * is a sentence the kitchen has to guess the subject of.
 *
 * Three properties, and each of them is because **this string is printed on a
 * kitchen ticket**:
 *
 *  - **one line.** Newlines, tabs and control characters become a single space,
 *    because a note with three line breaks in it pushes the next dish off the
 *    paper, and an ESC-prefixed string is an instruction to a thermal printer
 *    rather than a sentence to a cook.
 *  - **trimmed and collapsed**, so `"  no   onions "` and `"no onions"` are the
 *    same note and a rail does not merge two lines that differ by a space.
 *  - **refused above 200, never truncated.** A truncated note silently changes
 *    what a guest asked for, and half of "no shellfish, severe allergy" is
 *    worse than none of it. `orders.notes` takes 500 because it is one per
 *    ticket; this is one per dish and a paragraph on a line is not a note.
 *
 * It **never affects price** (B7 and §3.6): `priceOrder` carries it onto the
 * snapshot and never reads it.
 */
export const OrderLineNote = z
    .string()
    .max(200)
    .transform((raw) => raw
    // Written as escapes, not raw bytes: a raw NUL in a source file makes `grep`
    // call it binary and skip it (plan 2026-09-21, "second integration pass").
    // eslint-disable-next-line no-control-regex -- the point is the control characters
    .replace(/[\x00-\x1f\x7f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim());
export const OrderLineRequest = z.object({
    item_id: z.string().min(1).max(128),
    /**
     * **Which size** — an `item_options` id (migration 0055, design §9.6).
     *
     * An id and never a price, for the same reason `modifier_ids` is: the
     * option's `price_cents` is read from the catalog by `priceOrder`, and a
     * request that carried a number would fail to parse. Absent on a dish that
     * has sizes means **the default** (`defaultItemOption`, the one rule), so a
     * caller that never learned the word "option" is charged the headline price
     * and is never wrong — design §4.3's additivity, on the menu.
     */
    option_id: z.string().min(1).max(128).optional(),
    qty: z.number().int().min(1).max(99),
    /** Modifier ids, in any order. Duplicates are refused, not counted twice. */
    modifier_ids: z.array(z.string().min(1).max(128)).max(20).default([]),
    /**
     * What the kitchen should know about **this dish**. Optional, and `''` is
     * the same as absent — the column it lands in is `NOT NULL DEFAULT ''`, so
     * "no note" has one representation rather than two.
     */
    notes: OrderLineNote.optional(),
});
/** A basket. Empty is refused: an order for nothing is not an order. */
export const OrderBasket = z.array(OrderLineRequest).min(1).max(60);
const HHMM = /^(\d{2}):(\d{2})$/;
function minutesOf(hhmm) {
    const m = HHMM.exec(hhmm);
    if (m === null)
        return null;
    return Number(m[1]) * 60 + Number(m[2]);
}
/**
 * Is this dish orderable at this moment?
 *
 * The same half-open `[start, end)` comparison the engine makes about opening
 * hours, on the same `WeekHours` document, for the reason migration 0014 chose
 * that shape: *"is the lunch prix-fixe orderable at 13:40 on a Tuesday?" is "is
 * this open now?"*, and two shapes would need two answers that eventually
 * disagree.
 *
 * `null` means no window was ever set, which is the common case and the only one
 * that is distinguishable from an all-days-closed document — that means "not
 * currently on the menu" and answers `false` here.
 */
export function orderableAt(availability, clock) {
    if (availability === null)
        return true;
    const day = availability[clock.weekday];
    if (day === undefined || day.closed)
        return false;
    return day.windows.some((window) => {
        const start = minutesOf(window.start);
        const end = minutesOf(window.end);
        if (start === null || end === null)
            return false;
        return clock.minutes >= start && clock.minutes < end;
    });
}
/**
 * Price a basket against the menu, refusing anything the kitchen cannot make.
 *
 * Every refusal **names the dish**, because the caller on the other side of this
 * is a person on a phone or an agent reading aloud, and "invalid request" is not
 * a sentence either of them can act on. "We've just run out of the snapper" is.
 *
 * The order of the checks is fixed so the message a caller gets does not depend
 * on how a map happened to iterate: line by line in the order they were sent,
 * and within a line — quantity, item, 86 board, service window, then the
 * modifier tree.
 *
 * ### Which code, and why
 *
 * `invalid` (400) is a **request that could never have worked**: an item id that
 * is not on this tenant's menu, a modifier that belongs to a different dish, two
 * sides where the group says one. Spec §3.6 asks for exactly that word:
 * "rejects unknown combinations as `invalid`".
 *
 * `conflict` (409) is a request that **would have worked a moment ago**: an 86'd
 * dish, a modifier the kitchen has run out of, a lunch item at 22:40. The
 * agreement's meaning of `409` is "re-check and offer alternatives", which is
 * precisely what a caller should do with a sold-out entrée — and what it must
 * *not* do with a malformed basket.
 *
 * ### A line may be negative; a bill may not
 *
 * `modifiers.price_delta_cents` is signed so that "salad instead of fries, less
 * two dollars" is expressible (see `schema/catalog.ts`). The consequence is that
 * a single line can price below zero, and migration 0014's own comment says
 * where that is caught: *"A line may be negative; a bill may not, and that is
 * checked where an order is priced."* This is that place.
 */
export function priceOrder(catalog, basket, clock) {
    if (basket.length === 0) {
        throw ApiError.invalid('An order needs at least one item.', { reason: 'empty_order' });
    }
    const lines = [];
    for (const [index, requested] of basket.entries()) {
        /** Which line failed, on every refusal — a basket of twelve needs it. */
        const where = { line: index, item_id: requested.item_id };
        if (!Number.isInteger(requested.qty) || requested.qty < 1) {
            throw ApiError.invalid('A line is for one item or more.', {
                ...where,
                reason: 'bad_quantity',
                qty: requested.qty,
            });
        }
        const item = catalog.get(requested.item_id);
        if (item === undefined) {
            throw ApiError.invalid('That is not on this menu.', { ...where, reason: 'unknown_item' });
        }
        if (!item.active) {
            throw ApiError.invalid(`${item.name} is not on the menu.`, {
                ...where,
                reason: 'not_on_menu',
                item_name: item.name,
            });
        }
        if (item.soldOut) {
            throw ApiError.conflict(`The kitchen is out of ${item.name} tonight.`, {
                ...where,
                reason: 'sold_out',
                item_name: item.name,
            });
        }
        if (!orderableAt(item.availability, clock)) {
            throw ApiError.conflict(`${item.name} is not being served right now.`, {
                ...where,
                reason: 'not_available_now',
                item_name: item.name,
            });
        }
        const size = resolveSize(item, requested.option_id, where);
        const chosen = resolveModifiers(item, requested.modifier_ids, where);
        // `bookingPriceCents`' sentence, on a plate: *the option replaces, the
        // extras add.* The size's price stands in for the dish's headline and the
        // modifier deltas add to whichever of the two applied.
        const unitPriceCents = (size?.priceCents ?? item.priceCents) +
            chosen.reduce((sum, modifier) => sum + modifier.priceDeltaCents, 0);
        lines.push({
            itemId: item.id,
            itemName: item.name,
            optionId: size?.id ?? null,
            optionName: size?.name ?? null,
            qty: requested.qty,
            unitPriceCents,
            lineTotalCents: unitPriceCents * requested.qty,
            modifiers: chosen,
            // Carried onto the snapshot and never consulted above. The note is the
            // one field on a line that a caller writes and the server does not
            // check against the catalog, because there is nothing to check it
            // against — and nothing it can change.
            notes: requested.notes ?? '',
        });
    }
    const subtotalCents = lines.reduce((sum, line) => sum + line.lineTotalCents, 0);
    if (subtotalCents < 0) {
        // Only reachable through negative deltas, and only as a menu that gives more
        // away than it charges for. Refusing is the honest answer: an order we would
        // owe the guest money for is a menu bug, not a bill.
        throw ApiError.invalid('That order prices below zero.', {
            reason: 'negative_total',
            subtotal_cents: subtotalCents,
        });
    }
    return { lines, subtotalCents };
}
/**
 * **Which size this line means** — the one it named, or, when it named none and
 * the dish has sizes, **the default**.
 *
 * The same rule, in the same order, as `resolveOption` in `@yourstore/scheduling`
 * resolves a booking's option; it is restated here rather than imported because
 * `domain` depends on nothing (dev.md §3), and the shared half — *which one is
 * the default* — is already the one function both call, `defaultItemOption`.
 *
 * `undefined` for a dish with no live sizes, which is the whole fleet today and
 * is what makes an absent field byte-identical to before 0055.
 *
 * Three refusals, all `invalid`, all naming the line, because each is a request
 * that could never have worked (§3.6's word for exactly that):
 *
 *  - a size that belongs to another dish, or to nothing — the cross-dish case
 *    `resolveModifiers` already refuses for modifiers, and for the same reason:
 *    quietly substituting the default would sell a guest the Small she did not
 *    ask for at a price she did not see;
 *  - a size the kitchen has deactivated. `conflict` would be the honest word
 *    if this were the 86 board, but an inactive option is a catalog edit, not
 *    tonight's shortage, and `item_options` has no `sold_out` to make that
 *    distinction real. The detail names the id so a caller can re-read;
 *  - a size named on a dish that has none. Not a fallback to the dish's own
 *    price: an id this tenant's catalog cannot resolve is a stale or forged
 *    request, and a caller holding one has a menu that is not this one.
 */
function resolveSize(item, optionId, where) {
    const options = item.options ?? [];
    if (optionId === undefined) {
        // `defaultItemOption` reads the snake_case row shape every projection
        // shares; a two-key adapter is cheaper than a second rule.
        const chosen = defaultItemOption(options.map((o) => ({
            id: o.id,
            is_default: o.isDefault,
            sort_order: o.sortOrder,
            active: o.active,
        })));
        return chosen === null ? undefined : options.find((o) => o.id === chosen.id);
    }
    const named = options.find((o) => o.id === optionId);
    if (named === undefined) {
        throw ApiError.invalid(options.length === 0
            ? `${item.name} does not come in sizes.`
            : `That is not a size ${item.name} comes in.`, { ...where, reason: 'unknown_option', option_id: optionId, item_name: item.name });
    }
    if (!named.active) {
        throw ApiError.invalid(`${item.name} is no longer sold as ${named.name}.`, {
            ...where,
            reason: 'unknown_option',
            option_id: optionId,
            option_name: named.name,
            item_name: item.name,
        });
    }
    return named;
}
/**
 * The modifier tree, validated against the groups the schema already CHECKs.
 *
 * Migration 0014 put `min_select` / `max_select` / `required` on
 * `modifier_groups` and tied `required` to `min_select >= 1` with a constraint,
 * saying the rules would be *"enforceable without drift"*. This function is the
 * enforcement: it reads the same three columns, and because the constraint makes
 * `required` and `min_select` agree, there is only one rule to apply rather than
 * two that could disagree.
 */
function resolveModifiers(item, modifierIds, where) {
    const seen = new Set();
    for (const id of modifierIds) {
        if (seen.has(id)) {
            throw ApiError.invalid('That choice was made twice.', {
                ...where,
                reason: 'duplicate_modifier',
                modifier_id: id,
                item_name: item.name,
            });
        }
        seen.add(id);
    }
    /** modifier id → the group it belongs to, for this item only. */
    const owner = new Map();
    for (const group of item.groups) {
        for (const modifier of group.modifiers)
            owner.set(modifier.id, { group, modifier });
    }
    const chosen = [];
    const countByGroup = new Map();
    for (const id of modifierIds) {
        const found = owner.get(id);
        if (found === undefined) {
            // Includes the cross-dish case — a steak temperature asked for on a salad
            // — which is the shape of mistake a model makes most often.
            throw ApiError.invalid(`That is not a choice offered on ${item.name}.`, {
                ...where,
                reason: 'unknown_modifier',
                modifier_id: id,
                item_name: item.name,
            });
        }
        if (found.modifier.soldOut) {
            throw ApiError.conflict(`${found.modifier.name} is unavailable tonight.`, {
                ...where,
                reason: 'sold_out',
                modifier_id: id,
                modifier_name: found.modifier.name,
                item_name: item.name,
            });
        }
        countByGroup.set(found.group.id, (countByGroup.get(found.group.id) ?? 0) + 1);
        chosen.push({
            modifierId: found.modifier.id,
            name: found.modifier.name,
            priceDeltaCents: found.modifier.priceDeltaCents,
        });
    }
    for (const group of item.groups) {
        const count = countByGroup.get(group.id) ?? 0;
        if (count > group.maxSelect) {
            throw ApiError.invalid(`${group.name} takes at most ${String(group.maxSelect)} on ${item.name}.`, {
                ...where,
                reason: 'too_many_choices',
                group_id: group.id,
                group_name: group.name,
                item_name: item.name,
                max_select: group.maxSelect,
                chosen: count,
            });
        }
        // A group that is not required and was left alone is fine; the constraint
        // guarantees such a group has `min_select = 0`, so the same comparison
        // covers both cases without a second branch.
        if (count < group.minSelect) {
            throw ApiError.invalid(`${item.name} needs ${String(group.minSelect)} from ${group.name}.`, {
                ...where,
                reason: 'too_few_choices',
                group_id: group.id,
                group_name: group.name,
                item_name: item.name,
                min_select: group.minSelect,
                chosen: count,
            });
        }
    }
    return chosen;
}
/* ------------------------------------------------------------------ */
/* Money owed on an order                                              */
/* ------------------------------------------------------------------ */
/**
 * What is still to collect — the number a pay link for an order is priced at
 * (spec §3.7's `order` referent: "the order's server-computed total").
 *
 * One function rather than `total - prepaid` at each call site, for the same
 * reason `depositRequiredCents` is one function: two subtractions eventually
 * disagree about whether a partially-paid order owes the rest or the whole, and
 * the disagreement is money.
 */
export function orderOutstandingCents(order) {
    return Math.max(0, order.totalCents - order.prepaidCents);
}
//# sourceMappingURL=ordering.js.map